import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import type { User } from "../lib/types";
import { lectureById, type Lecture as LectureData } from "../data/lectures";
import { Sidebar } from "../components/Sidebar";
import { TopBar } from "../components/TopBar";
import { I } from "../components/Icons";
import { LECTURE_VISUALS } from "../components/LectureVisuals";
import { nymblLogoWhite } from "../assets";

// All lecture windows (your notes window + the audience popup) talk over one
// same-origin BroadcastChannel so advancing a slide in one moves the other.
const SYNC_CHANNEL = "gitfit-lecture";

interface SyncMessage {
  lectureId: string;
  idx: number;
}

interface Props {
  user: User;
  lectureId: string;
  onSignOut: () => void;
}

export function Lecture({ user, lectureId, onSignOut }: Props) {
  const [, setLocation] = useLocation();
  const lecture = lectureById(lectureId);

  // A locked lecture is closed to learners but stays open to admins (who do the
  // locking and still need to preview / present it). `null` means we haven't
  // heard back yet — fail open so a slow/failed fetch never blocks a learner.
  const [lockedForLearner, setLockedForLearner] = useState(false);
  useEffect(() => {
    if (user.isAdmin) return; // admins are never locked out
    let alive = true;
    fetch("/api/lectures/locks", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { locked: [] }))
      .then((data: { locked?: string[] }) => {
        if (alive) setLockedForLearner((data.locked ?? []).includes(lectureId));
      })
      .catch(() => {
        // Fail open — leave the lecture viewable for the learner.
      });
    return () => {
      alive = false;
    };
  }, [lectureId, user.isAdmin]);

  // `?present=1` is the audience view: slides only, no notes, no chrome — this
  // is the window you share. `i` seeds it with the slide you're on when you
  // open it; after that the two windows stay in sync over the channel.
  const search =
    typeof window === "undefined" ? "" : window.location.search;
  const params = new URLSearchParams(search);
  // Present mode is an admin-only tool. A learner who lands on a `?present=1`
  // URL just gets the normal read-only viewer — never the shareable deck.
  const isPresent = params.get("present") === "1" && user.isAdmin;
  const seedIdx = Math.max(0, parseInt(params.get("i") ?? "0", 10) || 0);

  const slideCount = lecture ? lecture.slides.length : 0;
  const [slideIdx, setSlideIdx] = useState(() =>
    Math.min(seedIdx, Math.max(0, slideCount - 1)),
  );

  // Broadcast local navigation to the other window. Received messages update
  // state directly (see below) and deliberately do NOT re-broadcast, so there's
  // no echo loop.
  const chanRef = useRef<BroadcastChannel | null>(null);
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    const chan = new BroadcastChannel(SYNC_CHANNEL);
    chan.onmessage = (e: MessageEvent<SyncMessage>) => {
      const d = e.data;
      if (d && d.lectureId === lectureId && typeof d.idx === "number") {
        setSlideIdx(Math.min(Math.max(d.idx, 0), Math.max(0, slideCount - 1)));
      }
    };
    chanRef.current = chan;
    return () => {
      chan.close();
      chanRef.current = null;
    };
  }, [lectureId, slideCount]);

  const go = useCallback(
    (next: number) => {
      const clamped = Math.min(Math.max(next, 0), Math.max(0, slideCount - 1));
      setSlideIdx(clamped);
      chanRef.current?.postMessage({ lectureId, idx: clamped });
    },
    [lectureId, slideCount],
  );

  // Arrow keys / space / page keys drive the deck from either window.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "ArrowRight" || e.key === "PageDown" || e.key === " ") {
        e.preventDefault();
        go(slideIdx + 1);
      } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
        e.preventDefault();
        go(slideIdx - 1);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, slideIdx]);

  function openPresenterView() {
    window.open(
      `/lecture/${lectureId}?present=1&i=${slideIdx}`,
      "gitfit-present",
      "width=1280,height=800",
    );
  }

  if (!lecture) {
    if (isPresent) {
      return (
        <div className="gf-present gf-present--empty">
          <p>That lecture doesn't exist.</p>
        </div>
      );
    }
    return (
      <div className="gf-shell">
        <Sidebar active="courses" user={user} onSignOut={onSignOut} />
        <div className="gf-main">
          <TopBar crumbs={[{ t: "Git Fit", href: "/dashboard" }, { t: "Lecture", strong: true }]} />
          <div className="gf-dash">
            <div className="gf-card">
              <div className="gf-card__body">
                <p>That lecture doesn't exist.</p>
                <button
                  className="gf-btn gf-btn--secondary"
                  onClick={() => setLocation("/dashboard")}
                >
                  Back to courses
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Locked out: learners get a friendly notice instead of the deck. Admins skip
  // this entirely (the effect above never sets the flag for them).
  if (lockedForLearner) {
    if (isPresent) {
      return (
        <div className="gf-present gf-present--empty">
          <p>This lecture is locked by your instructor.</p>
        </div>
      );
    }
    return (
      <div className="gf-shell">
        <Sidebar active="courses" user={user} onSignOut={onSignOut} />
        <div className="gf-main">
          <TopBar
            crumbs={[
              { t: "Git Fit", href: "/dashboard" },
              { t: "Lecture", strong: true },
            ]}
          />
          <div className="gf-dash">
            <div className="gf-card">
              <div className="gf-card__body">
                <p>
                  <I.Lock size={15} style={{ verticalAlign: "-2px", marginRight: 6 }} />
                  This lecture is locked by your instructor.
                </p>
                <button
                  className="gf-btn gf-btn--secondary"
                  onClick={() => setLocation("/dashboard")}
                >
                  Back to courses
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const slides = lecture.slides;
  const slide = slides[slideIdx];
  const progressPct = Math.round(((slideIdx + 1) / slides.length) * 100);

  // Audience view — this is the window you share. No notes, no nav chrome.
  if (isPresent) {
    return <PresentSlide lecture={lecture} idx={slideIdx} />;
  }

  return (
    <div className="gf-shell">
      <Sidebar active="courses" user={user} onSignOut={onSignOut} />
      <div className="gf-main">
        <TopBar
          crumbs={[
            { t: "Git Fit", href: "/dashboard" },
            { t: "My courses", href: "/dashboard" },
            { t: lecture.title, strong: true },
          ]}
          right={
            <button
              className="gf-btn gf-btn--ghost"
              onClick={() => setLocation("/dashboard")}
            >
              <I.ChevronLeft size={14} /> All courses
            </button>
          }
        />

        <div className="gf-lesson">
          <div className="gf-lesson__sidebar">
            <div className="gf-lesson__course-meta">
              {lecture.eyebrow}
            </div>
            <div className="gf-lesson__course-title">{lecture.title}</div>
            <div className="gf-lesson__course-sub">{lecture.coversLabel}</div>

            <div className="gf-lesson__steps">
              {slides.map((s, i) => {
                const cls = ["gf-lesson__step"];
                if (i === slideIdx) cls.push("is-active");
                if (i < slideIdx) cls.push("is-done");
                return (
                  <button
                    key={s.shortTitle}
                    className={cls.join(" ")}
                    onClick={() => go(i)}
                  >
                    <span className="gf-lesson__step-num">
                      {i < slideIdx ? <I.Check size={12} /> : i + 1}
                    </span>
                    <span className="gf-lesson__step-body">
                      <span className="gf-lesson__step-t">{s.shortTitle}</span>
                      <span className="gf-lesson__step-sub">{s.eyebrow}</span>
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="gf-lesson__progress">
              <div className="gf-lesson__progress-row">
                <span>Slide {slideIdx + 1} of {slides.length}</span>
                <span className="gf-lesson__progress-val">{progressPct}%</span>
              </div>
              <div className="gf-lesson__progress-bar">
                <div
                  className="gf-lesson__progress-bar-fill"
                  style={{ width: `${progressPct}%` }}
                ></div>
              </div>
            </div>
          </div>

          <div className="gf-lesson__body">
            <div className="gf-step-head">
              <div className="gf-step-head__left">
                <div className="gf-step-head__eyebrow">{slide.eyebrow}</div>
                <h1>{slide.heading}</h1>
              </div>
              <div className="gf-step-head__nav">
                {user.isAdmin && (
                  <button
                    className="gf-btn gf-btn--ghost"
                    onClick={openPresenterView}
                    title="Open a notes-free window to share on the call"
                  >
                    <I.Play size={14} /> Present
                  </button>
                )}
                <button
                  className="gf-btn gf-btn--secondary"
                  onClick={() => go(slideIdx - 1)}
                  disabled={slideIdx === 0}
                >
                  <I.ChevronLeft size={14} /> Back
                </button>
                <button
                  className="gf-btn gf-btn--gradient"
                  onClick={() => go(slideIdx + 1)}
                  disabled={slideIdx === slides.length - 1}
                >
                  Next slide <I.ArrowRight size={14} />
                </button>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
              {/* User-facing slide content — mirrors what the audience window shows. */}
              <div className="gf-card">
                <div className="gf-card__body">
                  {slide.visual && LECTURE_VISUALS[slide.visual] && (
                    <div className="gf-lecture__visual">
                      {LECTURE_VISUALS[slide.visual]!()}
                    </div>
                  )}
                  <ul className="gf-lecture__points">
                    {slide.points.map((p, i) => (
                      <li key={i}>{p}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Presenter script — admins only, never in the shared window. */}
              {user.isAdmin && (
                <div className="gf-lecture__notes">
                  <div className="gf-lecture__notes-head">
                    <I.Eye size={14} />
                    Speaking notes
                    <span className="gf-lecture__notes-tag">
                      Not shown on the shared screen
                    </span>
                  </div>
                  <ul className="gf-lecture__notes-list">
                    {slide.speakerNotes.map((n, i) => (
                      <li key={i}>{n}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// The shared, notes-free slide. Large type, navy background, full window — built
// to be the thing you screen-share.
function PresentSlide({ lecture, idx }: { lecture: LectureData; idx: number }) {
  const slide = lecture.slides[idx];
  const Visual = slide.visual ? LECTURE_VISUALS[slide.visual] : undefined;
  return (
    <div className="gf-present">
      {/* keyed by idx so the entrance animation replays on every slide change */}
      <div
        key={idx}
        className={`gf-present__inner${Visual ? " gf-present__inner--split" : ""}`}
      >
        <div className="gf-present__text">
          <div className="gf-present__eyebrow">{slide.eyebrow}</div>
          <h1 className="gf-present__heading">{slide.heading}</h1>
          <ul className="gf-present__points">
            {slide.points.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
        </div>
        {Visual && (
          <div className="gf-present__visual">
            <Visual />
          </div>
        )}
      </div>
      <div className="gf-present__footer">
        <img src={nymblLogoWhite} alt="Nymbl" className="gf-present__logo" />
        <div className="gf-present__count">
          {idx + 1} / {lecture.slides.length}
        </div>
      </div>
    </div>
  );
}
